import { z } from "zod";

// Wire primitives shared by more than one contract module. They live here rather than in
// index.ts so a sibling module (battles.ts) can import them without importing the barrel that
// re-exports it: ESM evaluates a cycle's later half against uninitialized bindings.

/** The description every amount field carries into /v1/openapi.json. */
export const CENTS = "Integer US cents; display through the environment's currency.";

export const MAX_CLIENT_SEED_LENGTH = 36;

export const clientSeedSchema = z.string().trim().min(1).max(MAX_CLIENT_SEED_LENGTH);

/**
 * Where an item comes from: the Loot OS catalog, a vendor marketplace listing, or a voucher the
 * house issued. `VOUCHER` reaches the wire on an inventory unit a cancelled marketplace order
 * paid back, never on a box a shopper can open.
 */
export const dropTableItemSourceSchema = z.enum(["CATALOG", "MARKETPLACE", "VOUCHER"]);
export type DropTableItemSource = z.infer<typeof dropTableItemSourceSchema>;

/**
 * `catalog_items.id`, what a drop-table row names when it is authored. A stored
 * `DropTableItem.itemId` is looser on purpose: versions published before drop tables named
 * catalog items hold free-form ids, and `isCatalogItemId` is how a reader tells the two apart.
 */
export const catalogItemIdSchema = z.uuid();

export function isCatalogItemId(value: string): boolean {
  return catalogItemIdSchema.safeParse(value).success;
}

export const dropTableItemSchema = z.object({
  itemId: z.string().min(1),
  itemSource: dropTableItemSourceSchema.describe(
    "Where the item comes from, and so what a winner receives. CATALOG is an item in the Loot OS catalog and itemId is its uuid; MARKETPLACE is a vendor listing bought for the shopper and itemId is the listing id. The roll reads weight alone, so this field does not change the odds. VOUCHER is sent only on an inventory unit a cancelled marketplace order paid back, never on a box entry.",
  ),
  name: z.string().min(1),
  imageUrl: z.string(),
  value: z.int().nonnegative().describe(CENTS),
  weight: z.int().nonnegative(),
  listingPriceCents: z
    .int()
    .nonnegative()
    .optional()
    .describe(
      `The vendor's asking price for a MARKETPLACE entry, absent on every other source. ${CENTS} It excludes the shipping Loot OS adds on top, so it is under value for such a vendor, and a withdrawal compares the live vendor price against this figure rather than against value.`,
    ),
  isLimited: z
    .boolean()
    .optional()
    .describe(
      "True when a counted number of this item can be won: every MARKETPLACE entry, and a catalog entry whose availability strategy is FIXED. Absent on an entry stored before the field existed.",
    ),
  limitedQuantity: z
    .int()
    .nonnegative()
    .nullable()
    .optional()
    .describe(
      "How many of a limited entry remain, as of the box's last refresh. Null on an unlimited entry. It is the one entry field outside the drop-table commitment, because stock moves without the box changing.",
    ),
  isGemSubstitute: z
    .boolean()
    .optional()
    .describe(
      "True when the item can no longer be shipped: no stock remains, the vendor listing is inactive, or the catalog item cannot be fulfilled. A shopper who wins it owns it like any other item, but a withdrawal is refused with 409 inventory_item_not_withdrawable, so the way to realize it is to sell it for its value.",
    ),
});
export type DropTableItem = z.infer<typeof dropTableItemSchema>;

export const seedRevealSchema = z.object({
  date: z.string(),
  seedHash: z.string(),
  /** null until the seed's UTC day has fully passed. */
  seed: z.string().nullable(),
});
export type SeedReveal = z.infer<typeof seedRevealSchema>;
